import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sendOTP } from "../../services/firebase";
import "./auth.css";

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep]                 = useState("phone");
  const [phone, setPhone]               = useState("");
  const [otp, setOtp]                   = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [err, setErr]                   = useState("");

  // ✅ recaptcha-container must exist in DOM before sendOTP is called
  // By putting it outside the conditional forms, it always stays mounted
  // We use useEffect to confirm it exists on load
  useEffect(() => {
    // Clear any stale recaptcha verifier on page load
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear(); } catch (e) {}
      window.recaptchaVerifier = null;
    }
  }, []);

  // ── Send OTP ─────────────────────────────────────────────
  const handleSendOTP = async (e) => {
    e?.preventDefault?.();
    setErr("");

    if (!phone || phone.length < 10) {
      setErr("Enter a valid 10-digit mobile number.");
      return;
    }

    try {
      setLoading(true);
      const result = await sendOTP("+91" + phone);
      setConfirmation(result);
      setStep("otp");
    } catch (error) {
      console.error("OTP send failed:", error.code, error.message);

      if (error.code === "auth/invalid-phone-number") {
        setErr("Invalid phone number. Please check and try again.");
      } else if (error.code === "auth/too-many-requests") {
        setErr("Too many attempts. Please wait a few minutes and try again.");
      } else if (error.code === "auth/operation-not-allowed") {
        setErr("Phone login not enabled. Contact support.");
      } else if (error.code === "auth/network-request-failed") {
        setErr("Network error. Check your internet connection.");
      } else {
        setErr("Failed to send OTP: " + (error.message || "Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP ───────────────────────────────────────────
  const handleVerifyOTP = async (e) => {
    e?.preventDefault?.();
    setErr("");

    if (!otp || otp.length < 4) {
      setErr("Enter the OTP sent to your phone.");
      return;
    }

    try {
      setLoading(true);
      await confirmation.confirm(otp);
      navigate(`/reset-password?phone=${phone}`);
    } catch (error) {
      console.error("OTP verify failed:", error.code, error.message);
      if (error.code === "auth/invalid-verification-code") {
        setErr("Wrong OTP. Please check and try again.");
      } else if (error.code === "auth/code-expired") {
        setErr("OTP expired. Please go back and request a new one.");
      } else {
        setErr("Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = step === "phone" ? 0 : 1;

  return (
    <div className="vl-page">

      {/* LEFT branding panel */}
      <div className="vl-left">
        <div className="vl-left-glow-1" />
        <div className="vl-left-glow-2" />
        <div className="vl-left-top">
          <div className="vl-logo">
            <div className="vl-logo-icon">
              <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Yuro Shopy" />
            </div>
            <span className="vl-logo-name">YURO SHOPY</span>
          </div>
        </div>
        <div className="vl-left-mid">
          <div className="vl-left-tag">Vendor Portal</div>
          <h1 className="vl-left-h1">Reset your<br /><em>password</em></h1>
          <p className="vl-left-p">
            Verify your phone number with an OTP and set a new password for your vendor account.
          </p>
          <div className="vl-stat-row">
            <div className="vl-stat"><span className="vl-stat-num">12K+</span><span className="vl-stat-lbl">Active Vendors</span></div>
            <div className="vl-stat-div" />
            <div className="vl-stat"><span className="vl-stat-num">98%</span><span className="vl-stat-lbl">Uptime SLA</span></div>
            <div className="vl-stat-div" />
            <div className="vl-stat"><span className="vl-stat-num">4.9★</span><span className="vl-stat-lbl">Satisfaction</span></div>
          </div>
        </div>
        <div className="vl-left-bot">
          {["Real-time sales analytics", "Inventory & order management", "Instant payout tracking"].map((f, i) => (
            <div className="vl-feat" key={i}>
              <div className="vl-feat-check">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT: Form panel */}
      <div className="vl-right">

        {/* Mobile branding */}
        <div className="vl-mob-brand">
          <div className="vl-logo-icon" style={{ width: 34, height: 34, borderRadius: 9 }}>
            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="Yuro Shopy" />
          </div>
          <span className="vl-logo-name">YURO SHOPY</span>
        </div>

        <div className="vl-mob-hero">
          <div className="vl-left-tag" style={{ marginBottom: 12 }}>Vendor Portal</div>
          <h2 className="vl-mob-h2">Reset your password</h2>
          <p className="vl-mob-p">Verify your phone and set a new password.</p>
        </div>

        {/* Form card */}
        <div className="vl-card">
          <div className="vl-card-label">Account Recovery</div>
          <h2 className="vl-card-title">
            {step === "phone" ? "Forgot Password?" : "Verify your phone"}
          </h2>
          <p className="vl-card-sub">
            {step === "phone"
              ? "Enter your registered mobile number to receive an OTP"
              : `OTP sent to +91 ${phone}`}
          </p>

          {/* Progress bar */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {["Mobile Number", "Verify OTP"].map((label, i) => (
              <div key={i} style={{
                flex: 1, height: 4, borderRadius: 4,
                background: i <= stepIndex ? "#6366f1" : "#e2e8f0",
                transition: "background 0.3s"
              }} />
            ))}
          </div>

          {/* Error */}
          {err && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 10, padding: "10px 14px", marginBottom: 16,
              color: "#dc2626", fontSize: 13,
              display: "flex", alignItems: "center", gap: 8
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {err}
            </div>
          )}

          {/* STEP 1: Phone */}
          {step === "phone" && (
            <form onSubmit={handleSendOTP}>
              <div className="vl-field">
                <label className="vl-lbl">Mobile Number</label>
                <div className="vl-iw">
                  <span className="vl-ii">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                    </svg>
                  </span>
                  <span style={{
                    position: "absolute", left: 40, top: "50%",
                    transform: "translateY(-50%)", fontSize: 14,
                    color: "#64748b", fontWeight: 600, userSelect: "none"
                  }}>+91</span>
                  <input
                    className="vl-inp"
                    style={{ paddingLeft: 68 }}
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="Enter 10-digit number"
                    inputMode="numeric"
                    maxLength={10}
                    required
                  />
                </div>
              </div>

              <button className="vl-btn" type="submit" disabled={loading}>
                {loading
                  ? <><span className="vl-spin" />Sending OTP…</>
                  : <>Send OTP <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg></>
                }
              </button>
            </form>
          )}

          {/* STEP 2: OTP */}
          {step === "otp" && (
            <form onSubmit={handleVerifyOTP}>
              <div className="vl-field">
                <label className="vl-lbl">One-Time Password</label>
                <div className="vl-iw">
                  <span className="vl-ii">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  </span>
                  <input
                    className="vl-inp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    required
                  />
                </div>
              </div>

              <button className="vl-btn" type="submit" disabled={loading}>
                {loading
                  ? <><span className="vl-spin" />Verifying…</>
                  : <>Verify OTP <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg></>
                }
              </button>

              <button
                type="button"
                onClick={() => { setStep("phone"); setOtp(""); setErr(""); }}
                style={{
                  width: "100%", marginTop: 10, padding: "10px",
                  background: "transparent", border: "1.5px solid #e2e8f0",
                  borderRadius: 10, fontSize: 13, color: "#64748b",
                  cursor: "pointer", fontWeight: 500
                }}
              >
                ← Change Number
              </button>
            </form>
          )}

          <div className="vl-footer">
            Remember your password? <Link to="/login">Sign in</Link>
          </div>
        </div>

        {/* ✅ recaptcha-container is OUTSIDE the conditional forms */}
        {/* This keeps it permanently in the DOM so Firebase can always find it */}
        <div id="recaptcha-container" />

        {/* Mobile feature pills */}
        <div className="vl-mob-feats">
          {["Sales analytics", "Order management", "Payout tracking"].map((f, i) => (
            <div className="vl-mob-pill" key={i}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {f}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}